/* eslint-disable max-lines */
import { createReducer, on } from '@ngrx/store';

import {
  Calculation,
  CostComponentSplitItem,
  CostComponentSplitType,
  ReferenceType,
  ReferenceTypeIdentifier,
  SchaefflerBomItem,
} from '@cdba/shared/models';
import {
  ComparisonSummary,
  DetailedComparisonSummary,
} from '@cdba/shared/models/comparison.model';

import {
  loadBom,
  loadBomFailure,
  loadBomSuccess,
  loadCalculationHistory,
  loadCalculationHistoryFailure,
  loadCalculationHistorySuccess,
  loadComparisonSummary,
  loadComparisonSummaryFailure,
  loadComparisonSummarySuccess,
  loadCostComponentSplit,
  loadCostComponentSplitFailure,
  loadCostComponentSplitSuccess,
  loadProductDetails,
  loadProductDetailsFailure,
  loadProductDetailsSuccess,
  selectBomItem,
  selectCalculation,
  toggleSplitType,
} from '../actions';
import { loadComparisonFeatureData } from '../actions/root/compare-root.actions';

export interface CompareState {
  [index: number]: {
    referenceType?: ReferenceTypeIdentifier;
    details?: {
      item: ReferenceType;
      loading: boolean;
      errorMessage: string;
    };
    calculations?: {
      items?: Calculation[];
      numberOfUnauthorizedCalculations?: number;
      missingCostRoles?: string[];
      unauthorizedPlants?: string[];
      selected?: Calculation;
      selectedNodeId?: string;
      loading?: boolean;
      errorMessage?: string;
    };
    // TODO: handle vitesco BOM comparison
    billOfMaterial?: {
      items?: SchaefflerBomItem[];
      // selected?: SchaefflerBomItem;
      selectedItemRowId?: string;
      loading?: boolean;
      errorMessage?: string;
    };
    costComponentSplit?: {
      loading: boolean;
      items: CostComponentSplitItem[];
      selectedSplitType: CostComponentSplitType;
      errorMessage: string;
    };
  };
  comparison?: ComparisonState;
}

export interface ComparisonState {
  summary: ComparisonSummary;
  detailedSummary: DetailedComparisonSummary;
  currency: string;
  loading: boolean;
  errorMessage?: string;
}

export const initialState: CompareState = {
  [0]: undefined,
  [1]: undefined,
  comparison: undefined,
};

export const compareReducer = createReducer(
  initialState,
  on(loadComparisonFeatureData, (_state, { items }): CompareState => {
    const state: CompareState = {
      comparison: undefined,
    };

    items.forEach((item, index: number) => {
      state[index] = {
        referenceType: item.referenceTypeIdentifier,
        calculations: { selectedNodeId: item.selectedCalculationId },
      };
    });

    return state;
  }),
  on(
    loadProductDetails,
    (state, { index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              details: {
                ...state[index].details,
                item: undefined,
                errorMessage: undefined,
                loading: true,
              },
            },
          }
        : state
  ),
  on(
    loadProductDetailsSuccess,
    (state, { item, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              details: {
                ...state[index].details,
                item,
                loading: false,
              },
            },
          }
        : state
  ),
  on(
    loadProductDetailsFailure,
    (state, { errorMessage, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              details: {
                ...state[index].details,
                errorMessage,
                item: undefined,
                loading: false,
              },
            },
          }
        : state
  ),
  on(
    loadBom,
    (state, { index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              billOfMaterial: {
                loading: true,
              },
            },
          }
        : state
  ),
  on(
    loadBomSuccess,
    (state, { items, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              billOfMaterial: {
                ...state[index].billOfMaterial,
                items,
                selectedItemRowId: items.find((item) => item.path.length === 1)
                  .rowId,
                loading: false,
              },
            },
            comparison: {
              loading: false,
              summary: undefined,
              detailedSummary: undefined,
              currency: undefined,
              errorMessage: undefined,
            },
          }
        : state
  ),
  on(
    loadBomFailure,
    (state, { errorMessage, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              billOfMaterial: {
                errorMessage,
                items: [],
                loading: false,
              },
            },
          }
        : state
  ),
  on(
    selectBomItem,
    (state, { selectedItemRowId, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              billOfMaterial: {
                ...state[index].billOfMaterial,
                selectedItemRowId,
              },
            },
          }
        : state
  ),
  on(
    loadCalculationHistory,
    (state, { index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              calculations: {
                ...state[index].calculations,
                items: undefined,
                numberOfUnauthorizedCalculations: undefined,
                missingCostRoles: undefined,
                selected: undefined,
                loading: true,
              },
              billOfMaterial: {
                ...state[index].billOfMaterial,
                selectedItemRowId: undefined,
                items: undefined,
                loading: true,
              },
            },
          }
        : state
  ),
  on(
    loadCalculationHistorySuccess,
    (
      state,
      {
        items,
        missingCostRoles,
        numberOfUnauthorizedCalculations,
        unauthorizedPlants,
        plant,
        index,
      }
    ): CompareState => {
      if (!state[index]) {
        return state;
      }

      if (items?.length === 0) {
        return {
          ...state,
          [index]: {
            ...state[index],
            billOfMaterial: { loading: false },
            calculations: {
              ...state[index].calculations,
              items,
              missingCostRoles,
              numberOfUnauthorizedCalculations,
              unauthorizedPlants,
              selectedNodeId: undefined,
              selected: undefined,
              loading: false,
            },
          },
        };
      }

      const selectedCalculation = plant
        ? items.find((item) => item.plant === plant)
        : items[0];

      const nodeId: string = (
        items.indexOf(selectedCalculation) || 0
      ).toString();

      const selectedNodeId: string =
        state[index].calculations?.selectedNodeId || nodeId;

      return {
        ...state,
        [index]: {
          ...state[index],
          calculations: {
            ...state[index].calculations,
            items,
            missingCostRoles,
            numberOfUnauthorizedCalculations,
            unauthorizedPlants,
            selectedNodeId,
            selected: items[+selectedNodeId],
            loading: false,
          },
        },
      };
    }
  ),
  on(
    loadCalculationHistoryFailure,
    (state, { errorMessage, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              calculations: {
                ...state[index].calculations,
                errorMessage,
                items: [],
                numberOfUnauthorizedCalculations: undefined,
                missingCostRoles: undefined,
                loading: false,
              },
              billOfMaterial: {
                ...state[index].billOfMaterial,
                errorMessage,
                items: [],
                loading: false,
              },
            },
          }
        : state
  ),
  on(
    selectCalculation,
    (state, { nodeId, calculation, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              calculations: {
                ...state[index].calculations,
                selectedNodeId: nodeId,
                selected: calculation,
              },
            },
          }
        : state
  ),
  on(
    loadCostComponentSplit,
    (state: CompareState, { index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              costComponentSplit: {
                loading: true,
                items: undefined,
                errorMessage: undefined,
                selectedSplitType: CostComponentSplitType.MAIN,
              },
            },
          }
        : state
  ),
  on(
    loadCostComponentSplitSuccess,
    (state: CompareState, { items, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              costComponentSplit: {
                ...state[index].costComponentSplit,
                items,
                loading: false,
              },
            },
          }
        : state
  ),
  on(
    loadCostComponentSplitFailure,
    (state: CompareState, { errorMessage, index }): CompareState =>
      state[index]
        ? {
            ...state,
            [index]: {
              ...state[index],
              costComponentSplit: {
                ...state[index].costComponentSplit,
                errorMessage,
                items: [],
                loading: false,
              },
            },
          }
        : state
  ),
  on(toggleSplitType, (state: CompareState): CompareState => {
    const newState: CompareState = {
      comparison: {
        detailedSummary: undefined,
        summary: undefined,
        currency: undefined,
        loading: true,
      },
    };

    Object.keys(state).forEach((index) => {
      if (state[+index]?.costComponentSplit) {
        newState[+index] = {
          ...state[+index],
          costComponentSplit: {
            ...state[+index].costComponentSplit,
            selectedSplitType:
              state[+index].costComponentSplit.selectedSplitType ===
              CostComponentSplitType.MAIN
                ? CostComponentSplitType.AUX
                : CostComponentSplitType.MAIN,
          },
        };
      }
    });

    return newState;
  }),
  on(
    loadComparisonSummary,
    (state: CompareState): CompareState => ({
      ...state,
      comparison: {
        detailedSummary: undefined,
        summary: undefined,
        currency: undefined,
        loading: true,
      },
    })
  ),
  on(
    loadComparisonSummarySuccess,
    (state: CompareState, { comparison }): CompareState => ({
      ...state,
      comparison: {
        detailedSummary: comparison.detailedSummary,
        summary: comparison.summary,
        currency: comparison.currency,
        loading: false,
        errorMessage: '',
      },
    })
  ),
  on(
    loadComparisonSummaryFailure,
    (state: CompareState, { errorMessage, statusCode }): CompareState => ({
      ...state,
      comparison: {
        detailedSummary: undefined,
        summary: undefined,
        currency: undefined,
        loading: false,
        errorMessage: `${statusCode} ${errorMessage}`,
      },
    })
  )
);
